import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, MoreHorizontal, Edit, Trash2, Eye, QrCode, Award, Gift, Network, Users, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { validateEmail, validatePhone, validateName } from '@/utils/inputValidation'
import { useAuth } from '@/hooks/useAuth'
import ReferralMindmap from '@/components/admin/ReferralMindmap'
import StampFilter, { StampFilterType } from '@/components/admin/StampFilter'
import StampFilterSettings from '@/components/admin/StampFilterSettings'
import { UsageLimitAlert } from '@/components/subscription/UsageLimitAlert'
import { PremiumFeatureGate } from '@/components/subscription/PremiumFeatureGate'
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess'

interface Customer {
  id: string
  full_name: string
  first_name?: string
  last_name?: string
  email: string
  phone?: string
  points: number
  total_visits: number
  last_visit?: string
  created_at: string
  visit_stamps: number
  referral_stamps: number
  referral_code: string
  total_referrals: number
  user_id?: string | null
}

interface CustomerStamp {
  id: string
  stamp_type: string
  points_awarded: number
  created_at: string
  created_by?: string
}

const CustomerManagement = () => {
  const navigate = useNavigate()
  const { tenantId, userRole, user } = useAuth()
  const { canAddCustomers, canAddStamps } = useSubscriptionAccess()
  
  console.log('CustomerManagement - tenantId:', tenantId, 'userRole:', userRole, 'user:', user?.email)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [customerStamps, setCustomerStamps] = useState<CustomerStamp[]>([])
  const [filteredStamps, setFilteredStamps] = useState<CustomerStamp[]>([])
  const [stampFilter, setStampFilter] = useState<StampFilterType>('all')
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referredBy: '',
    referralCode: ''
  })
  const [editCustomer, setEditCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  })
  const [rewardPoints, setRewardPoints] = useState(1)
  const [stampType, setStampType] = useState<'visit' | 'referral'>('visit')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { toast } = useToast()

  useEffect(() => {
    console.log('🔍 CustomerManagement useEffect triggered')
    console.log('📊 Current auth state:', { 
      tenantId, 
      userRole, 
      userEmail: user?.email,
      userId: user?.id 
    })
    
    if (tenantId && userRole === 'admin') {
      console.log('✅ Conditions met - fetching customers')
      fetchCustomers()
    } else {
      console.log('❌ Not fetching customers:', {
        hasTenantId: !!tenantId,
        isAdmin: userRole === 'admin',
        actualRole: userRole
      })
    }
  }, [tenantId, userRole, user])

  useEffect(() => {
    // Filter customers based on search term
    if (searchTerm.trim() === '') {
      setFilteredCustomers(customers)
    } else {
      const filtered = customers.filter(customer =>
        customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredCustomers(filtered)
    }
  }, [customers, searchTerm])

  useEffect(() => {
    // Filter stamps based on selected filter
    if (stampFilter === 'all') {
      setFilteredStamps(customerStamps)
    } else {
      setFilteredStamps(customerStamps.filter(stamp => stamp.stamp_type === stampFilter))
    }
  }, [customerStamps, stampFilter])

  const fetchCustomers = async () => {
    console.log('fetchCustomers called with tenantId:', tenantId)
    
    if (!tenantId) {
      console.error('No tenantId available')
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Kein Tenant gefunden. Bitte melden Sie sich erneut an."
      })
      return
    }
    
    try {
      setLoading(true)
      console.log('Fetching customers with secure function for tenant:', tenantId)
      
      // Use secure function to get customers with proper phone number masking
      const { data, error } = await supabase.rpc('get_customers_extra_secure') as { 
        data: any[] | null, 
        error: any 
      };

      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      // Filter for current tenant and map to expected format
      const tenantCustomers = (data || [])
        .filter((customer: any) => customer.tenant_id === tenantId)
        .map((customer: any) => ({
          id: customer.id,
          full_name: customer.full_name,
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email,
          phone: customer.phone, // Already masked by the secure function
          points: customer.points,
          total_visits: customer.total_visits,
          last_visit: customer.last_visit,
          created_at: customer.created_at,
          visit_stamps: customer.visit_stamps,
          referral_stamps: customer.referral_stamps,
          referral_code: customer.referral_code,
          total_referrals: customer.total_referrals
        }))
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      console.log('Customers fetched securely:', tenantCustomers.length, 'customers')
      setCustomers(tenantCustomers)
      await auditLogger.logDataAccess('customers', tenantId, 'view')
    } catch (error: any) {
      console.error('Error fetching customers:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: `Unterstützer konnten nicht geladen werden: ${error.message}`
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomerStamps = async (customerId: string) => {
    if (!tenantId) return
    
    try {
      const { data, error } = await supabase
        .from('stamps')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false })

      if (error) throw error

      setCustomerStamps(data || [])
    } catch (error: any) {
      console.error('Error fetching customer stamps:', error)
    }
  }

  const validateCustomerForm = () => {
    const newErrors: Record<string, string> = {}

    if (!validateName(newCustomer.firstName)) {
      newErrors.firstName = 'Vorname ist erforderlich (max. 50 Zeichen)'
    }

    if (!validateName(newCustomer.lastName)) {
      newErrors.lastName = 'Nachname ist erforderlich (max. 50 Zeichen)'
    }

    if (!validateEmail(newCustomer.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }

    if (newCustomer.phone && !validatePhone(newCustomer.phone)) {
      newErrors.phone = 'Bitte geben Sie eine gültige deutsche Telefonnummer ein'
    }

    // Check for duplicate email
    if (customers.some(c => c.email === newCustomer.email)) {
      newErrors.email = 'Ein Unterstützer mit dieser E-Mail-Adresse existiert bereits'
    }

    return newErrors
  }

  const sendCustomerInvitation = async (customerId: string, customerEmail: string) => {
    if (!tenantId) return
    
    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('send-customer-invitation', {
        body: {
          customerId,
          tenantId,
        },
      })

      if (error) throw error

      toast({
        title: "Einladung versendet",
        description: `Eine Passwort-Setup-Email wurde an ${customerEmail} gesendet.`,
      })

      await auditLogger.logAdminAction('send_customer_invitation', 'customers', customerId, {
        customer_email: customerEmail,
      })

    } catch (error: any) {
      console.error('Error sending customer invitation:', error)
      toast({
        title: "Fehler",
        description: error.message || 'Einladung konnte nicht versendet werden',
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const createCustomer = async () => {
    if (!tenantId) return
    
    const validationErrors = validateCustomerForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    setLoading(true)

    try {
      const { data: newCustomerId, error } = await supabase
        .rpc('create_customer_admin_secure', {
          p_tenant_id: tenantId,
          p_first_name: newCustomer.firstName.trim(),
          p_last_name: newCustomer.lastName.trim(),
          p_email: newCustomer.email.toLowerCase().trim(),
          p_phone: newCustomer.phone.trim() || null,
        })

      if (error) throw error

      await auditLogger.logAdminAction('create_customer', 'customers', undefined, {
        customer_name: `${newCustomer.firstName} ${newCustomer.lastName}`,
        customer_email: newCustomer.email
      })

      toast({
        title: "Unterstützer erstellt",
        description: `${newCustomer.firstName} ${newCustomer.lastName} wurde erfolgreich hinzugefügt.`
      })

      // Automatically send invitation to newly created customer
      if (newCustomerId) {
        await sendCustomerInvitation(newCustomerId, newCustomer.email.toLowerCase().trim())
      }

      // Reset form and refresh data
      setNewCustomer({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        referredBy: '',
        referralCode: ''
      })
      setIsCreateModalOpen(false)
      fetchCustomers()

    } catch (error: any) {
      console.error('Error creating customer:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Erstellen",
        description: error.message || "Der Unterstützer konnte nicht erstellt werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const addStamp = async (customerId: string, customerName: string, stampType: string = 'visit') => {
    if (!tenantId) return
    
    try {
      // Add stamp with specified type
      const { error: stampError } = await supabase
        .from('stamps')
        .insert({
          tenant_id: tenantId,
          customer_id: customerId,
          stamp_type: stampType,
          points_awarded: 1
        })

      if (stampError) throw stampError

      await auditLogger.logAdminAction('add_stamp', 'stamps', customerId, {
        customer_name: customerName,
        stamp_type: stampType,
        points_awarded: 1
      })

      toast({
        title: "Stempel vergeben",
        description: `${customerName} hat einen ${stampType === 'visit' ? 'Besuchs' : 'Empfehlungs'}stempel erhalten.`
      })

      fetchCustomers()

    } catch (error: any) {
      console.error('Error adding stamp:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Stempel vergeben",
        description: error.message || "Der Stempel konnte nicht vergeben werden."
      })
    }
  }

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('de-DE')
  }

  const getPointsBadgeColor = (points: number) => {
    if (points >= 20) return 'bg-purple-100 text-purple-800 hover:bg-purple-100'
    if (points >= 10) return 'bg-blue-100 text-blue-800 hover:bg-blue-100'
    if (points >= 5) return 'bg-green-100 text-green-800 hover:bg-green-100'
    return 'bg-gray-100 text-gray-800 hover:bg-gray-100'
  }

  const handleInputChange = (field: string, value: string) => {
    setNewCustomer(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleEditInputChange = (field: string, value: string) => {
    setEditCustomer(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer)
    fetchCustomerStamps(customer.id)
    setIsDetailsModalOpen(true)
  }

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer)
    setEditCustomer({
      firstName: customer.first_name || '',
      lastName: customer.last_name || '',
      email: customer.email,
      phone: customer.phone || ''
    })
    setErrors({})
    setIsEditModalOpen(true)
  }

  const handleRewardCustomer = (customer: Customer) => {
    setSelectedCustomer(customer)
    setRewardPoints(1)
    setIsRewardModalOpen(true)
  }

  const handleDeleteCustomer = (customer: Customer) => {
    setSelectedCustomer(customer)
    setIsDeleteDialogOpen(true)
  }

  const validateEditForm = () => {
    const newErrors: Record<string, string> = {}

    if (!validateName(editCustomer.firstName)) {
      newErrors.firstName = 'Vorname ist erforderlich (max. 50 Zeichen)'
    }

    if (!validateName(editCustomer.lastName)) {
      newErrors.lastName = 'Nachname ist erforderlich (max. 50 Zeichen)'
    }

    if (!validateEmail(editCustomer.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
    }

    if (editCustomer.phone && !validatePhone(editCustomer.phone)) {
      newErrors.phone = 'Bitte geben Sie eine gültige deutsche Telefonnummer ein'
    }

    // Check for duplicate email (excluding current customer)
    if (customers.some(c => c.email === editCustomer.email && c.id !== selectedCustomer?.id)) {
      newErrors.email = 'Ein anderer Unterstützer mit dieser E-Mail-Adresse existiert bereits'
    }

    return newErrors
  }

  const updateCustomer = async () => {
    if (!tenantId || !selectedCustomer) return
    
    const validationErrors = validateEditForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    setLoading(true)

    try {
      const { error } = await supabase
        .from('customers')
        .update({
          first_name: editCustomer.firstName.trim(),
          last_name: editCustomer.lastName.trim(),
          full_name: `${editCustomer.firstName.trim()} ${editCustomer.lastName.trim()}`.trim(),
          email: editCustomer.email.toLowerCase().trim(),
          phone: editCustomer.phone.trim() || null
        })
        .eq('id', selectedCustomer.id)

      if (error) throw error

      await auditLogger.logAdminAction('update_customer', 'customers', selectedCustomer.id, {
        customer_name: `${editCustomer.firstName} ${editCustomer.lastName}`,
        customer_email: editCustomer.email
      })

      toast({
        title: "Unterstützer aktualisiert",
        description: `${editCustomer.firstName} ${editCustomer.lastName} wurde erfolgreich aktualisiert.`
      })

      setIsEditModalOpen(false)
      fetchCustomers()

    } catch (error: any) {
      console.error('Error updating customer:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Aktualisieren",
        description: error.message || "Der Unterstützer konnte nicht aktualisiert werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const awardReward = async () => {
    if (!tenantId || !selectedCustomer) return
    
    try {
      setLoading(true)

      // Get current customer data first
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select('points')
        .eq('id', selectedCustomer.id)
        .single()

      if (fetchError) throw fetchError

      // Add reward stamp
      const { error: stampError } = await supabase
        .from('stamps')
        .insert({
          tenant_id: tenantId,
          customer_id: selectedCustomer.id,
          points_awarded: rewardPoints,
          stamp_type: 'reward'
        })

      if (stampError) throw stampError

      // Update customer points
      const { error: updateError } = await supabase
        .from('customers')
        .update({
          points: customer.points + rewardPoints
        })
        .eq('id', selectedCustomer.id)

      if (updateError) throw updateError

      await auditLogger.logAdminAction('award_reward', 'stamps', selectedCustomer.id, {
        customer_name: selectedCustomer.full_name,
        points_awarded: rewardPoints
      })

      toast({
        title: "Belohnung vergeben",
        description: `${selectedCustomer.full_name} hat ${rewardPoints} Bonuspunkte erhalten.`
      })

      setIsRewardModalOpen(false)
      fetchCustomers()

    } catch (error: any) {
      console.error('Error awarding reward:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Belohnen",
        description: error.message || "Die Belohnung konnte nicht vergeben werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteCustomer = async () => {
    if (!tenantId || !selectedCustomer) return
    
    try {
      setLoading(true)

      // Delete all stamps first
      const { error: stampsError } = await supabase
        .from('stamps')
        .delete()
        .eq('customer_id', selectedCustomer.id)

      if (stampsError) throw stampsError

      // Delete customer
      const { error: customerError } = await supabase
        .from('customers')
        .delete()
        .eq('id', selectedCustomer.id)

      if (customerError) throw customerError

      await auditLogger.logAdminAction('delete_customer', 'customers', selectedCustomer.id, {
        customer_name: selectedCustomer.full_name,
        customer_email: selectedCustomer.email
      })

      toast({
        title: "Unterstützer gelöscht",
        description: `${selectedCustomer.full_name} wurde erfolgreich gelöscht.`
      })

      setIsDeleteDialogOpen(false)
      fetchCustomers()

    } catch (error: any) {
      console.error('Error deleting customer:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Löschen",
        description: error.message || "Der Unterstützer konnte nicht gelöscht werden."
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Unterstützer verwalten</h2>
            <p className="text-muted-foreground">
              Verwalten Sie Ihre Unterstützer und deren Treuepunkte
            </p>
          </div>
          
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Neuer Unterstützer
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Neuen Unterstützer hinzufügen</DialogTitle>
                <DialogDescription>
                  Fügen Sie einen neuen Unterstützer zu Ihrem Treueprogramm hinzu
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="firstName">Vorname *</Label>
                  <Input
                    id="firstName"
                    value={newCustomer.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Max"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-destructive">{errors.firstName}</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="lastName">Nachname *</Label>
                  <Input
                    id="lastName"
                    value={newCustomer.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Mustermann"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-destructive">{errors.lastName}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="email">E-Mail-Adresse *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="max@mustermann.de"
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email}</p>
                  )}
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="phone">Telefonnummer (optional)</Label>
                  <Input
                    id="phone"
                    value={newCustomer.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="0123 456789"
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">{errors.phone}</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="referredBy">Empfohlen durch (optional)</Label>
                  <Input
                    id="referredBy"
                    value={newCustomer.referredBy}
                    onChange={(e) => handleInputChange('referredBy', e.target.value)}
                    placeholder="Name des empfehlenden Unterstützers"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="referralCode">Empfehlungscode (optional)</Label>
                  <Input
                    id="referralCode"
                    value={newCustomer.referralCode}
                    onChange={(e) => handleInputChange('referralCode', e.target.value)}
                    placeholder="Empfehlungscode eingeben"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={createCustomer} disabled={loading}>
                  {loading ? 'Erstelle...' : 'Unterstützer hinzufügen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs for different views */}
        <Tabs defaultValue="customers" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="customers">Unterstützer verwalten</TabsTrigger>
            <TabsTrigger value="mindmap">
              <Network className="h-4 w-4 mr-2" />
              Empfehlungsnetzwerk
            </TabsTrigger>
            <TabsTrigger value="stamps">Filter-Einstellungen</TabsTrigger>
          </TabsList>
          
          <TabsContent value="customers" className="space-y-4">
            <PremiumFeatureGate 
              feature="hasAdvancedCRM" 
              fallback={
                <Card>
                  <CardHeader>
                    <CardTitle>Basis Unterstützerverwaltung</CardTitle>
                    <CardDescription>
                      Upgrade für erweiterte CRM-Funktionen
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                      Erweiterte CRM-Funktionen sind im Pro Plan verfügbar
                    </div>
                  </CardContent>
                </Card>
              }
            >
            {/* Search and Customer Table */}
            <Card>
              <CardHeader>
                <CardTitle>Unterstützer ({filteredCustomers.length})</CardTitle>
                <CardDescription>
                  Übersicht aller registrierten Unterstützer
                </CardDescription>
              </CardHeader>
              <CardContent>
            <div className="flex items-center space-x-2 mb-4">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name, E-Mail oder Telefon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unterstützer</TableHead>
                    <TableHead>Kontakt</TableHead>
                    <TableHead>Besuche</TableHead>
                    <TableHead>Geworbene Kunden</TableHead>
                    <TableHead>Letzter Besuch</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Lade Unterstützer...
                      </TableCell>
                    </TableRow>
                  ) : filteredCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchTerm ? 'Keine Unterstützer gefunden.' : 'Noch keine Unterstützer registriert.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{customer.full_name}</div>
                            <div className="text-sm text-muted-foreground">
                              Unterstützer seit {formatDate(customer.created_at)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="text-sm">{customer.email}</div>
                            {customer.phone && (
                              <div className="text-sm text-muted-foreground">{customer.phone}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {customer.total_visits}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {customer.total_referrals || 0} Kunden
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {customer.last_visit ? formatDate(customer.last_visit) : 'Nie'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" disabled={loading}>
                                  <QrCode className="h-4 w-4 mr-1" />
                                  Stempel
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Stempel vergeben</DialogTitle>
                                  <DialogDescription>
                                    Wählen Sie die Art des Stempels für {customer.full_name}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label>Stempeltyp</Label>
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                      <Button
                                        variant={stampType === 'visit' ? 'default' : 'outline'}
                                        onClick={() => setStampType('visit')}
                                        className="h-20 flex-col"
                                      >
                                        <QrCode className="h-6 w-6 mb-2" />
                                        Besuchsstempel
                                      </Button>
                                      <Button
                                        variant={stampType === 'referral' ? 'default' : 'outline'}
                                        onClick={() => setStampType('referral')}
                                        className="h-20 flex-col"
                                      >
                                        <Users className="h-6 w-6 mb-2" />
                                        Empfehlungsstempel
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button
                                    onClick={() => addStamp(customer.id, customer.full_name, stampType)}
                                    disabled={loading}
                                  >
                                    {loading ? 'Vergebe...' : 'Stempel vergeben'}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewDetails(customer)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Details anzeigen
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditCustomer(customer)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Bearbeiten
                                </DropdownMenuItem>
                                {!customer.user_id && (
                                  <DropdownMenuItem 
                                    onClick={() => sendCustomerInvitation(customer.id, customer.email)}
                                    disabled={loading}
                                  >
                                    <Mail className="mr-2 h-4 w-4" />
                                    Einladung senden
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleRewardCustomer(customer)}>
                                  <Award className="mr-2 h-4 w-4" />
                                  Belohnung vergeben
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => handleDeleteCustomer(customer)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Löschen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
            </PremiumFeatureGate>
          </TabsContent>

      <TabsContent value="mindmap">
        <PremiumFeatureGate feature="hasAdvancedCRM">
          <ReferralMindmap tenantId={tenantId || ''} />
        </PremiumFeatureGate>
      </TabsContent>

      <TabsContent value="stamps" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <StampFilterSettings tenantId={tenantId || ''} />
        </div>
        
        <div className="lg:col-span-2">
          <StampFilter
            currentFilter={stampFilter}
            onFilterChange={setStampFilter}
            visitCount={customerStamps.filter(s => s.stamp_type === 'visit').length}
            referralCount={customerStamps.filter(s => s.stamp_type === 'referral').length}
            totalCount={customerStamps.length}
          />
        </div>
      </TabsContent>
    </Tabs>

        {/* Edit Customer Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Unterstützer bearbeiten</DialogTitle>
              <DialogDescription>
                Ändern Sie die Unterstützerinformationen
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="editFirstName">Vorname *</Label>
                <Input
                  id="editFirstName"
                  value={editCustomer.firstName}
                  onChange={(e) => handleEditInputChange('firstName', e.target.value)}
                  placeholder="Max"
                />
                {errors.firstName && (
                  <p className="text-sm text-destructive">{errors.firstName}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="editLastName">Nachname *</Label>
                <Input
                  id="editLastName"
                  value={editCustomer.lastName}
                  onChange={(e) => handleEditInputChange('lastName', e.target.value)}
                  placeholder="Mustermann"
                />
                {errors.lastName && (
                  <p className="text-sm text-destructive">{errors.lastName}</p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="editEmail">E-Mail-Adresse *</Label>
                <Input
                  id="editEmail"
                  type="email"
                  value={editCustomer.email}
                  onChange={(e) => handleEditInputChange('email', e.target.value)}
                  placeholder="max@mustermann.de"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="editPhone">Telefonnummer (optional)</Label>
                <Input
                  id="editPhone"
                  value={editCustomer.phone}
                  onChange={(e) => handleEditInputChange('phone', e.target.value)}
                  placeholder="0123 456789"
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone}</p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={updateCustomer} disabled={loading}>
                {loading ? 'Aktualisiere...' : 'Änderungen speichern'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Customer Details Modal */}
        <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Unterstützerdetails</DialogTitle>
              <DialogDescription>
                Vollständige Übersicht über {selectedCustomer?.full_name}
              </DialogDescription>
            </DialogHeader>
            
            {selectedCustomer && (
              <div className="grid gap-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Name</Label>
                    <p className="font-medium">{selectedCustomer.full_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">E-Mail</Label>
                    <p>{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Telefon</Label>
                    <p>{selectedCustomer.phone || 'Nicht angegeben'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Referral Code</Label>
                    <p className="font-mono text-sm">{selectedCustomer.referral_code}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Gesamtpunkte</Label>
                    <p className="text-lg font-semibold text-primary">{selectedCustomer.points}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Gesamtbesuche</Label>
                    <p className="text-lg font-semibold">{selectedCustomer.total_visits}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Letzter Besuch</Label>
                    <p>{selectedCustomer.last_visit ? formatDate(selectedCustomer.last_visit) : 'Nie'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Unterstützer seit</Label>
                    <p>{formatDate(selectedCustomer.created_at)}</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Stempel-Historie ({customerStamps.length})
                    </Label>
                    <StampFilter
                      currentFilter={stampFilter}
                      onFilterChange={setStampFilter}
                      visitCount={customerStamps.filter(s => s.stamp_type === 'visit').length}
                      referralCount={customerStamps.filter(s => s.stamp_type === 'referral').length}
                      totalCount={customerStamps.length}
                    />
                  </div>
                  <div className="border rounded-md">
                    <div className="max-h-48 overflow-y-auto">
                      {filteredStamps.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          {stampFilter === 'all' ? 'Noch keine Stempel vergeben' : 
                           `Keine ${stampFilter === 'visit' ? 'Besuchs' : 'Empfehlungs'}-Stempel`}
                        </div>
                      ) : (
                        <div className="divide-y">
                          {filteredStamps.map((stamp) => (
                            <div key={stamp.id} className="p-3 flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                {stamp.stamp_type === 'reward' ? (
                                  <Gift className="h-4 w-4 text-purple-600" />
                                ) : stamp.stamp_type === 'referral' ? (
                                  <Gift className="h-4 w-4 text-accent" />
                                ) : (
                                  <QrCode className="h-4 w-4 text-green-600" />
                                )}
                                <div>
                                  <p className="text-sm font-medium">
                                    {stamp.stamp_type === 'reward' ? 'Belohnung' : 
                                     stamp.stamp_type === 'referral' ? 'Empfehlung' : 'Besuch'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatDate(stamp.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium">
                                  +{stamp.points_awarded} Punkte
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={() => setIsDetailsModalOpen(false)}>
                Schließen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reward Customer Modal */}
        <Dialog open={isRewardModalOpen} onOpenChange={setIsRewardModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Belohnung vergeben</DialogTitle>
              <DialogDescription>
                Vergeben Sie Bonuspunkte an {selectedCustomer?.full_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="rewardPoints">Anzahl Punkte</Label>
                <Input
                  id="rewardPoints"
                  type="number"
                  min="1"
                  max="100"
                  value={rewardPoints}
                  onChange={(e) => setRewardPoints(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                />
                <p className="text-xs text-muted-foreground">
                  Zwischen 1 und 100 Punkte möglich
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsRewardModalOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={awardReward} disabled={loading}>
                {loading ? 'Vergebe...' : `${rewardPoints} Punkte vergeben`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Customer Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unterstützer löschen</AlertDialogTitle>
              <AlertDialogDescription>
                Sind Sie sicher, dass Sie <strong>{selectedCustomer?.full_name}</strong> löschen möchten? 
                Diese Aktion kann nicht rückgängig gemacht werden. Alle Stempel und Punkte dieses Unterstützers werden ebenfalls gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteCustomer}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {loading ? 'Lösche...' : 'Endgültig löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}

export default CustomerManagement